/**
 * Legal-entity-scoped attention counts for sidebar / tab badges.
 *
 * Registry `countSource` callbacks receive `{ legalEntityId }` from
 * `useAttentionSignals` once the workspace LE context is ready.
 * Every path here must include `legal_entity_id` when the API requires it.
 */

import { api } from "./api";

export interface AttentionCountContext {
  legalEntityId: string;
}

export type AttentionCountFn = (
  ctx: AttentionCountContext,
) => Promise<number>;

/** Build query string with required LE scope + optional filters. */
export function attentionQuery(
  legalEntityId: string,
  params: Record<string, string | number | undefined> = {},
): string {
  const qs = new URLSearchParams({
    legal_entity_id: legalEntityId,
  });
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      qs.set(key, String(value));
    }
  }
  return qs.toString();
}

function scopedList(
  path: string,
  legalEntityId: string,
  params: Record<string, string | number | undefined> = {},
): Promise<number> {
  return api.countOf(`${path}?${attentionQuery(legalEntityId, params)}`);
}

/** Named count sources — wired from navigation/registry.ts. */
export const attentionCounts = {
  crossWorkflowApprovals: (ctx: AttentionCountContext) =>
    scopedList("/api/mission-control/pending-approvals", ctx.legalEntityId, {
      limit_per_type: 100,
    }),

  leaveRequestsSubmitted: (ctx: AttentionCountContext) =>
    scopedList("/api/leave/requests", ctx.legalEntityId, {
      state: "submitted",
    }),

  leaveRequestsPendingApproval: (ctx: AttentionCountContext) =>
    scopedList("/api/leave/requests", ctx.legalEntityId, {
      state: "pending_approval",
    }),

  benefitEnrollmentsPendingReview: (ctx: AttentionCountContext) =>
    scopedList("/api/benefit-enrollments", ctx.legalEntityId, {
      status: "pending_review",
    }),

  dependentVerificationsPending: (ctx: AttentionCountContext) =>
    scopedList("/api/dependent-verifications", ctx.legalEntityId, {
      review_status: "pending",
    }),

  providerInvoicesDiscrepancy: (ctx: AttentionCountContext) =>
    scopedList("/api/provider-invoices", ctx.legalEntityId, {
      status: "discrepancy",
    }),

  benefitClaimsSubmitted: (ctx: AttentionCountContext) =>
    scopedList("/api/benefit-claims", ctx.legalEntityId, {
      status: "submitted",
    }),

  renewalPricingFlaggedVariance: (ctx: AttentionCountContext) =>
    scopedList("/api/renewal-pricing", ctx.legalEntityId, {
      status: "flagged_variance",
    }),

  loanApplicationsPendingApproval: (ctx: AttentionCountContext) =>
    scopedList("/api/loans/applications", ctx.legalEntityId, {
      state: "pending_approval",
    }),

  loanApplicationsApproved: (ctx: AttentionCountContext) =>
    scopedList("/api/loans/applications", ctx.legalEntityId, {
      state: "approved",
    }),

  loanArrears: (ctx: AttentionCountContext) =>
    scopedList("/api/loans/arrears", ctx.legalEntityId),

  excuseRequestsPendingApproval: (ctx: AttentionCountContext) =>
    scopedList("/api/excuse-requests", ctx.legalEntityId, {
      state: "pending_approval",
    }),

  /** Matches mission-control `termination_open` (multi-state, not a single `state=`). */
  eosOpenTerminations: async (ctx: AttentionCountContext) => {
    try {
      const headlines = await api.missionControlHeadlines(
        ctx.legalEntityId,
      );
      const card = headlines.cards.find((c) => c.key === "termination_open");
      return card?.count ?? 0;
    } catch {
      return 0;
    }
  },
} satisfies Record<string, AttentionCountFn>;
